/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { EMPTY } from 'rxjs';

import { TicketList } from 'src/app/common/fancy-firebase-ticket/lists/TicketList';
import { PlanState } from 'src/app/services/plan-state.service';

import { UndoItem } from './UndoItem';
import { UndoTypes, UndoTitles } from './UndoTypes';
import { BaseUndoOperation } from './base-undo-operation';

import { TicketMeta } from 'src/app/app-data/models/TicketMeta';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';

type UndoDataType = [
	{ deletedTickets: TicketMeta[]; dependencyTickets: TicketMeta[] },
	{ deletedTickets: TicketMeta[]; dependencyTickets: TicketMeta[] }
];

interface IDeleteInvalidationCache {}

/** type the data and invalidation cache fields of the default UndoItem */
interface IDeleteUndoItem extends UndoItem {
	//interesting thing, this one doesn't "need" to store a after... because the after is null. Maybe it should for consistency? Not sure
	data: UndoDataType;
	invalidationCache: IDeleteInvalidationCache;
}
//data: {deleteTickets: }

export class TicketDeleteOperation extends BaseUndoOperation<IDeleteInvalidationCache, IDeleteUndoItem> {
	public readonly undoType = UndoTypes.ticketDelete;
	//can subclass this class and override this property for most of "undo moved to floating"

	//interface
	public title = UndoTitles.ticketDelete;

	//pass in state info
	constructor(
		private ticketList: TicketList,
		private planState: PlanState,
		private featureFlagManager: FeatureFlagManager
	) {
		super();
	}

	private makeUndoItem(data: UndoDataType): IDeleteUndoItem {
		// debugger;
		return {
			title: this.title,
			data,
			undoType: this.undoType,
			invalidationCache: this.makeInvalidationCache(data),
		};
	}

	private makeInvalidationCache(data: UndoDataType): IDeleteInvalidationCache {
		return {};
	}

	// public snapshotUndoState(data: Array<Ticket>) {
	public snapshotUndoState(data: UndoDataType) {
		// this.onPush(this.makeUndoItem(data[0].deletedTickets));
		this.onPush(this.makeUndoItem(data));
	}

	//...likely want a variant that doesn't use TicketMeta and make this elsewhere
	private simpleApplySnapshot(list: TicketMeta[]) {
		if (list && list.length) {
			list.forEach((ticketMeta) => {
				const targetTicketList = ticketMeta.ticket.isTimelineTicket
					? this.planState.timelineTickets
					: this.ticketList;
				this.planState.actions.updateTicketLiveData(
					ticketMeta.ticket.$id,
					ticketMeta.ticket.view,
					targetTicketList,
					{ overwrite: true }
				);
				this.planState.actions.persistTicketData(ticketMeta.ticket, targetTicketList);
			});
		}
	}

	public applyUndo(undoItem: IDeleteUndoItem) {
		// debugger;
		const cacheData = undoItem.data[0];
		const deletedTickets = cacheData.deletedTickets;
		const dependencyTickets = cacheData.dependencyTickets;

		//deleted tickets
		if (deletedTickets && deletedTickets.length) {
			deletedTickets.forEach((ticketMeta) => {
				this.planState.actions.createTicketWithId(
					ticketMeta.ticket.$id,
					ticketMeta.ticket.prepareDataForSave()
				);
				// Restore custom fields:
				this.planState.customFieldsApi.createCustomFieldTicketValues(
					ticketMeta.ticket.$id,
					ticketMeta.customFieldTicketValues
				);
				// Restore attachments:
				if (
					this.featureFlagManager.getFlag(DynamicFlagTypes.FILE_ATTACH_ENABLED) &&
					ticketMeta.ticketAttachmentsData
				) {
					this.planState.fileAttachCloneService.copyAttachment$(
						this.planState.projectId,
						this.planState.planId,
						ticketMeta.ticket.$id,
						ticketMeta.ticketAttachmentsData
					);
				}
			});
		}

		//dependency tickets
		this.simpleApplySnapshot(dependencyTickets);

		//restore timeline ticket
		if (deletedTickets && deletedTickets.length) {
			deletedTickets.forEach((ticketMeta) => {
				if (ticketMeta.timelineIdx) {
					const split = this.planState.timelineTickets.splitId(ticketMeta.timelineIdx);
					this.planState.legacyActions.toggleTimelineTicket(split[0], split[1], true, true);
				}
			});
		}

		return EMPTY; //placeholder, since updateTicketLiveData runs more or less immediately
	}

	// private deleteSoakingTimeout;

	public applyRedo(undoItem: IDeleteUndoItem) {
		const deletedTickets = undoItem.data[0].deletedTickets; //use the pre-delete list since the post delete is null;
		const dependencyTickets = undoItem.data[1].dependencyTickets;

		//deleted tickets
		if (deletedTickets && deletedTickets.length) {
			const deMetad = deletedTickets.map((m) => m.ticket);
			this.planState.actions.internalDeleteTicket(deMetad);
			// note: custom field deletion is already handled in by ticketEventHandling() in plan-state.service triggered by internalDeleteTicket()
		}

		//dependency tickets
		this.simpleApplySnapshot(dependencyTickets);

		return EMPTY; //placeholder, since updateTicketLiveData runs more or less immediately
	}

	// interface - ehhhh
	// just always recalc for now...
	public calculateCombinedInvalidation(list: IDeleteUndoItem[], existingCache: IDeleteInvalidationCache) {
		return {};
	}
}

//Write up
// undo deletion phases
// - replace event based with manual trigger

// when deleting (new thing only)
// - delete ticketExceptionDays

// when snapshotting an undo
// - need ticket data of all deleted ticket before
// - need ticket data of all dependency updated tickets before and after
// - need a way to differentiate between the two types of ticket data (property and same list, or seperate lists?)
// - need timeline data for the deleted tickets (or both if that's easier)
// - need ticket exception day data for the deleted tickets (or both if that's easier)

// const isOnNow = this.planState.legacyActions.toggleTimelineTicket(planId, ticketId);

// when applying an undo
// - create tickets from the previous undo's deleted list
// - update tickets from the previous undo's dependency edited list
// - update timeline ticket data to match what is stored in the ticketMeta
// - restore ticket exception days from ticketMeta on the deleted list

// when applying a redo
// - delete tickets from the post delete list
// - update tickets from the post delete dependency edited list
// - update timeline ticket data to match what is stored in the ticketMeta for deleted list (maybe remove is explicit?). Plan-state may automatically clean these up.
// - delete ticket exception days. Plan-state may do this already

// when invalidating
// - when anything in the dependency updated list is deleted
// - cache needs to maintain that list
